import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  ScrollView,
  TouchableOpacity,
  Switch,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { Button } from '@/components/common/Button';
import { useAuthStore } from '@/store/authStore';
import { FeedbackModal } from '@/components/feedback/FeedbackModal';
import { supabase } from '@/services/supabase/supabaseClient';

/**
 * Profile Screen
 * User profile and account management
 */
export const ProfileScreen: React.FC = () => {
  const { user, logout, isLoading } = useAuthStore();
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [mentionsOnly, setMentionsOnly] = useState(false);
  const [loadingPrefs, setLoadingPrefs] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Load notification preferences
  useEffect(() => {
    loadNotificationPreferences();
  }, [user?.id]);

  const loadNotificationPreferences = async () => {
    if (!user?.id) return;

    try {
      console.log(`📋 [Profile] Loading notification preferences for user: ${user.id}`);

      const { data, error } = await supabase
        .from('profiles')
        .select('notification_preferences')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      const prefs = data?.notification_preferences as any;
      if (prefs) {
        setNotificationsEnabled(prefs.enabled ?? true);
        setMentionsOnly(prefs.mentions_only ?? false);
        console.log(`✅ [Profile] Loaded preferences:`, prefs);
      }
    } catch (error) {
      console.error(`❌ [Profile] Failed to load preferences:`, error);
    } finally {
      setLoadingPrefs(false);
    }
  };

  const updateNotificationPreferences = async (enabled: boolean, mentions: boolean) => {
    if (!user?.id) return;

    setUpdating(true);
    try {
      console.log(`💾 [Profile] Updating notification preferences:`, { enabled, mentions });

      const { error } = await supabase
        .from('profiles')
        .update({
          notification_preferences: {
            enabled,
            mentions_only: mentions,
          },
        })
        .eq('id', user.id);

      if (error) throw error;

      console.log(`✅ [Profile] Successfully updated preferences`);
    } catch (error: any) {
      console.error(`❌ [Profile] Failed to update preferences:`, error);
      Alert.alert('Error', 'Failed to update notification settings');
      // Revert changes
      loadNotificationPreferences();
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleNotifications = async (value: boolean) => {
    setNotificationsEnabled(value);
    await updateNotificationPreferences(value, mentionsOnly);
  };

  const handleToggleMentionsOnly = async (value: boolean) => {
    setMentionsOnly(value);
    await updateNotificationPreferences(notificationsEnabled, value);
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <StatusBar
        barStyle="light-content"
        backgroundColor={Colors.background.primary}
        translucent={true}
      />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.content}>
        <View style={styles.header}>
          <View style={styles.avatarContainer}>
            <Text style={styles.avatarText}>{user?.name?.charAt(0).toUpperCase() || '?'}</Text>
          </View>
          <Text style={styles.name}>{user?.name}</Text>
          <Text style={styles.email}>{user?.email}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Account Info</Text>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>User ID:</Text>
            <Text style={styles.infoValue}>{user?.id.slice(0, 8)}...</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Member Since:</Text>
            <Text style={styles.infoValue}>
              {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
            </Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Notifications</Text>
          {loadingPrefs ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="small" color={Colors.accent.primary} />
              <Text style={styles.loadingText}>Loading preferences...</Text>
            </View>
          ) : (
            <>
              <View style={styles.settingRow}>
                <View style={styles.settingInfo}>
                  <Ionicons name="notifications" size={24} color={Colors.accent.primary} />
                  <View style={styles.settingTextContainer}>
                    <Text style={styles.settingLabel}>Push Notifications</Text>
                    <Text style={styles.settingDescription}>
                      Receive notifications for group messages
                    </Text>
                  </View>
                </View>
                <Switch
                  value={notificationsEnabled}
                  onValueChange={handleToggleNotifications}
                  trackColor={{ false: Colors.border.subtle, true: Colors.accent.primary }}
                  thumbColor={Colors.text.primary}
                  disabled={updating}
                />
              </View>

              <View style={[styles.settingRow, styles.settingRowLast]}>
                <View style={styles.settingInfo}>
                  <Ionicons name="at" size={24} color={Colors.ai.primary} />
                  <View style={styles.settingTextContainer}>
                    <Text style={styles.settingLabel}>Mentions Only</Text>
                    <Text style={styles.settingDescription}>Only notify when you're mentioned</Text>
                  </View>
                </View>
                <Switch
                  value={mentionsOnly}
                  onValueChange={handleToggleMentionsOnly}
                  trackColor={{ false: Colors.border.subtle, true: Colors.ai.primary }}
                  thumbColor={Colors.text.primary}
                  disabled={updating || !notificationsEnabled}
                />
              </View>

              {updating && (
                <View style={styles.updatingBanner}>
                  <ActivityIndicator size="small" color={Colors.accent.primary} />
                  <Text style={styles.updatingText}>Updating...</Text>
                </View>
              )}
            </>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Help & Feedback</Text>
          <TouchableOpacity
            style={styles.menuItem}
            onPress={() => setShowFeedbackModal(true)}
            activeOpacity={0.7}
          >
            <Ionicons name="chatbox-ellipses" size={24} color={Colors.accent.primary} />
            <Text style={styles.menuItemText}>Send Feedback</Text>
            <Ionicons name="chevron-forward" size={20} color={Colors.text.tertiary} />
          </TouchableOpacity>
        </View>

        <View style={styles.buttonContainer}>
          <Button
            title="Logout"
            onPress={handleLogout}
            variant="secondary"
            loading={isLoading}
            disabled={isLoading}
            fullWidth
          />
        </View>
      </ScrollView>

      {/* Feedback Modal */}
      <FeedbackModal
        visible={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        screenName="ProfileScreen"
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 24,
  },
  header: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  avatarContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: Colors.accent.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  avatarText: {
    fontSize: 32,
    fontWeight: 'bold',
    color: Colors.text.primary,
  },
  name: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Colors.text.primary,
    marginBottom: 4,
  },
  email: {
    fontSize: 14,
    color: Colors.text.secondary,
  },
  section: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 12,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  infoLabel: {
    fontSize: 14,
    color: Colors.text.secondary,
  },
  infoValue: {
    fontSize: 14,
    color: Colors.text.primary,
    fontWeight: '500',
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    gap: 12,
  },
  menuItemText: {
    flex: 1,
    fontSize: 16,
    color: Colors.text.primary,
    fontWeight: '500',
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    gap: 12,
  },
  loadingText: {
    fontSize: 14,
    color: Colors.text.secondary,
  },
  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  settingRowLast: {
    borderBottomWidth: 0,
  },
  settingInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
  },
  settingTextContainer: {
    flex: 1,
  },
  settingLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.text.primary,
    marginBottom: 2,
  },
  settingDescription: {
    fontSize: 13,
    color: Colors.text.secondary,
  },
  updatingBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: `${Colors.accent.primary}10`,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    marginTop: 8,
    gap: 8,
  },
  updatingText: {
    fontSize: 13,
    color: Colors.accent.primary,
    fontWeight: '500',
  },
  buttonContainer: {
    marginTop: 24,
  },
});
